import {
  Button,
  Checkbox,
  CloseButton,
  Flex,
  Heading,
  IconInfoSolid,
  Modal,
  RadioInput,
  RadioInputGroup,
  Spinner,
  TextArea,
  Tooltip,
} from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { EffectiveTermSelect } from "../../LearningOutcomes/EffectiveTermSelect";
import { pendingArchive } from "../../LearningOutcomes/utils";

const EditOutcomeModal = ({ open, setOpen, outcome, handleSave }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [newName, setNewName] = useState(outcome.name);
  const [newStatement, setNewStatement] = useState(outcome.statement);
  const [variety, setVariety] = useState(outcome.outcome_variety ?? "outcome");
  const [version, setVersion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [effectiveTerm, setEffectiveTerm] = useState(undefined);

  useEffect(() => {
    if (!version) {
      setEffectiveTerm(undefined);
    }
  }, [version]);

  useEffect(() => {
    if (newName === outcome?.name && newStatement === outcome?.statement) {
      setVersion(false);
    }
  }, [outcome, newName, newStatement]);

  const handleSaveOutcome = async () => {
    try {
      setLoading(true);
      await handleSave(newName, newStatement, variety, version, effectiveTerm);
      setOpen(false);
    } catch (e) {
      showNotification(e.message, ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      size="medium"
      label={
        outcome?.name
          ? t("analyze.editLearningOutcome")
          : t("analyze.addLearningOutcome")
      }
      onExited={() => {
        setNewName(outcome?.name || "");
        setNewStatement(outcome?.statement || "");
        setVariety(outcome?.outcome_variety || "outcome");
        setVersion(false);
        setEffectiveTerm(undefined);
      }}
    >
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Flex.Item>
            <Heading level="h2" as="h1">
              {outcome?.name
                ? t("analyze.editLearningOutcome")
                : t("analyze.addLearningOutcome")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={() => setOpen(false)}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap" margin="0 0 medium 0">
          <Flex.Item width="100%">
            <TextArea
              label={t("analyze.learningOutcomeName")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              messages={
                newName.length > 255
                  ? [
                      {
                        text: t("form.exceedsLimit", {
                          item: t("analyze.learningOutcomeName"),
                          limit: 255,
                        }),
                        type: "error",
                      },
                    ]
                  : []
              }
            />
          </Flex.Item>
          <Flex.Item padding="medium 0 0 0" shouldGrow>
            <TextArea
              label={
                <div style={{ width: 600 }}>
                  {t("analyze.learningOutcomeStatement")}{" "}
                  {outcome?.name && (
                    <Tooltip
                      color="primary-inverse"
                      renderTip={
                        <div style={{ maxWidth: 600 }}>
                          {outcome.outcome_type === "CSLO"
                            ? t("analyze.statementTooltip")
                            : "Please keep direct edits of the outcome statement minimal if you are not defining a new outcome version."}
                        </div>
                      }
                      placement="top"
                      on={["hover", "focus"]}
                    >
                      <IconInfoSolid size="x-small" color="brand" />
                    </Tooltip>
                  )}
                </div>
              }
              value={newStatement}
              onChange={(e) => setNewStatement(e.target.value)}
              messages={
                newStatement.length > 1500
                  ? [
                      {
                        text: t("form.exceedsLimit", {
                          item: t("analyze.learningOutcomeStatement"),
                          limit: 1500,
                        }),
                        type: "error",
                      },
                    ]
                  : []
              }
            />
          </Flex.Item>
          {outcome?.outcome_type !== "CSLO" && (
            <Flex.Item shouldGrow margin="medium 0 0 0">
              <RadioInputGroup
                layout="columns"
                name="outcome_variety"
                value={variety}
                onChange={(_e, val) => setVariety(val)}
                description={t("learningOutcomes.outcomeVariety") + ":"}
              >
                <RadioInput value="outcome" label={t("ratingScale.lo")} />
                <RadioInput
                  value="standard"
                  label={t("learningOutcomes.standard")}
                />
              </RadioInputGroup>
            </Flex.Item>
          )}
          {outcome?.name &&
            outcome?.outcome_type !== "CSLO" &&
            !pendingArchive(outcome) && (
              <Flex.Item align="end">
                <Checkbox
                  label={t("review.createNewVersion") + "?"}
                  onChange={(e) => setVersion(e.target.checked)}
                  checked={version}
                  disabled={
                    newName === outcome?.name &&
                    newStatement === outcome?.statement
                  }
                />
              </Flex.Item>
            )}
        </Flex>
        {version && (
          <EffectiveTermSelect
            selectHandler={(term) => setEffectiveTerm(term)}
            selectedTermId={effectiveTerm?.id}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small 0 0" onClick={() => setOpen(false)}>
          {t("global.cancel")}
        </Button>
        <Button
          type="submit"
          color="primary"
          onClick={handleSaveOutcome}
          disabled={
            loading ||
            newName.length === 0 ||
            newStatement.length === 0 ||
            newName.length > 255 ||
            newStatement.length > 1500
          }
        >
          {loading ? (
            <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
          ) : (
            t("global.save")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditOutcomeModal;
